import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import CONFIG from "../../config";
import { useAuth } from "../../context/AuthProvider";
import { usePatient } from "../../context/PatientContext";
import { useTheme } from "../../context/ThemeContext";
import TransliteratedInput from "../common/TransliteratedInput";

const AddAttendant = () => {
  const [formData, setFormData] = useState({
    MANDALI_FULL_NAME: "",
    MANDALI_MOB_NO: "",
    MANDALI_PASSWORD: "",
    CONFIRM_PASSWORD: "",
    MANDALI_ROLE: 3,
    CLINIC_IDS: [] // ✅ array for multiple clinics
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { verifyTokenAndRedirect } = useAuth();
  const [clinics, setClinics] = useState([]);
  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const token = localStorage.getItem("authToken");
  const { doctorInfo } = usePatient();
  const { theme, isDarkMode } = useTheme();

  // Fetch all clinics for the dropdown
  useEffect(() => {
    verifyTokenAndRedirect();
    axios.get(`${baseURL}/doctors/clinics`, {
      headers: {
        Authorization: `Bearer ${token}`,
        doctorId: doctorInfo.MANDALI_ID,
        clinicId: doctorInfo.CLINIC_ID,
        roleId: doctorInfo.MANDALI_ROLE
      }
    })
      .then((res) => setClinics(res.data))
      .catch((error) => console.error("Error fetching clinics:", error));
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Submit form data to the backend
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password and confirm password
    if (formData.MANDALI_PASSWORD !== formData.CONFIRM_PASSWORD) {
      alert("Passwords do not match!");
      return;
    }

    try {
      const { CONFIRM_PASSWORD, ...dataToSend } = formData;
      await axios.post(`${baseURL}/doctors/add`, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          doctorId: doctorInfo.MANDALI_ID,
          clinicId: doctorInfo.CLINIC_ID,
          roleId: doctorInfo.MANDALI_ROLE
        }
      });
      alert("Attendant added successfully!");
      navigate("/doctorslist");
    } catch (error) {
      console.error("Error adding attendant:", error);
      alert("Failed to add attendant. Please try again.");
    }
  };

  return (
    <div
      className="min-h-screen w-full transition-colors duration-700 ease-in-out flex flex-col items-center justify-center p-4 md:p-8"
      style={{
        background: 'transparent',
        color: theme.text
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-4xl rounded-[2.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] transition-all duration-500 overflow-hidden"
        style={{
          backgroundColor: theme.card,
        }}
      >
        {/* Header Section */}
        <div className="px-8 md:px-12 pt-8 md:pt-10 pb-6 border-b" style={{ borderColor: theme.border }}>
          <h2
            className="text-2xl md:text-3xl mb-2 tracking-tight"
            style={{
              fontFamily: theme.fonts?.serif,
              color: theme.text
            }}
          >
            Add New Attendant
          </h2>
          <div className="h-1 w-20 rounded-full" style={{ backgroundColor: theme.primary }} />
          <p className="text-sm mt-4 font-medium" style={{ color: theme.textSecondary }}>
            Fill in the details below to register a new attendant in the system.
          </p>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="px-8 md:px-12 py-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Attendant Name <span className="text-red-500">*</span>
              </label>
              <TransliteratedInput
                name="MANDALI_FULL_NAME"
                value={formData.MANDALI_FULL_NAME}
                onChangeText={(text) => setFormData({ ...formData, MANDALI_FULL_NAME: text })}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                placeholder="Enter attendant full name"
                required
              />
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="MANDALI_MOB_NO"
                value={formData.MANDALI_MOB_NO}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                placeholder="Enter mobile number"
                required
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Clinics (Select one or more) <span className="text-red-500">*</span>
              </label>
              <select
                name="CLINIC_IDS"
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                  minHeight: '100px'
                }}
                value={formData.CLINIC_IDS}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                  setFormData({ ...formData, CLINIC_IDS: selectedOptions });
                }}
                multiple
                required
              >
                {clinics.map((clinic) => (
                  <option key={clinic.CLINIC_ID} value={clinic.CLINIC_ID}>
                    {clinic.CLINIC_NAME}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-gray-400 mt-1">Hold Ctrl (Cmd on Mac) to select multiple</p>
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="MANDALI_PASSWORD"
                  value={formData.MANDALI_PASSWORD}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 pr-12 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                  placeholder="Create password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-lg hover:scale-110 transition-transform"
                  style={{ color: theme.textSecondary }}
                >
                  {showPassword ? "🙈" : "👁️"}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  name="CONFIRM_PASSWORD"
                  value={formData.CONFIRM_PASSWORD}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 pr-12 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                  style={{
                    backgroundColor: theme.inputBg,
                    borderColor: theme.border,
                    color: theme.text,
                  }}
                  placeholder="Repeat password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-lg hover:scale-110 transition-transform"
                  style={{ color: theme.textSecondary }}
                >
                  {showConfirm ? "🙈" : "👁️"}
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end items-center gap-4 pt-6 mt-6 border-t" style={{ borderColor: theme.border }}>
            <button
              type="button"
              onClick={() => navigate("/doctorslist")}
              className="px-6 py-2.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              style={{
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.text,
                border: `1px solid ${theme.border}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-2.5 rounded-full font-bold text-white transition-all duration-200 hover:scale-105 active:scale-95 text-sm md:text-base shadow-lg"
              style={{
                backgroundImage: `linear-gradient(to right, ${theme.brownTwo}, ${theme.secondary}, ${theme.primary}, ${theme.brownTwo})`,
                backgroundSize: '200% auto',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundPosition = 'right center'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundPosition = 'left center'}
            >
              Add Attendant
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddAttendant;